/**
 * Groups API client
 * Endpoints for per-admin Empresa/Sector grouping (user-groups tree)
 * and per-agent assignment.
 */

import apiClient from '@/api/client';
import type { AgentAssignment, Subgroup, UpdateAssignmentRequest, UserGroup } from '@/types/groups';

export const groupsApi = {
  /**
   * List the calling admin's groups.
   * @param include When "subgroups", embeds subgroups + agentCount per group
   *                via 2 batch queries server-side (no N+1).
   */
  listGroups: async (include?: 'subgroups'): Promise<UserGroup[]> => {
    const qs = include === 'subgroups' ? '?include=subgroups' : '';
    const response = await apiClient.get<UserGroup[]>(`/user-groups${qs}`);
    return response.data;
  },

  createGroup: async (body: {
    name: string;
    color?: string;
    icon?: string;
    position?: number;
  }): Promise<UserGroup> => {
    const response = await apiClient.post<UserGroup>('/user-groups', body);
    return response.data;
  },

  getGroup: async (groupId: string): Promise<UserGroup> => {
    const response = await apiClient.get<UserGroup>(`/user-groups/${groupId}`);
    return response.data;
  },

  updateGroup: async (
    groupId: string,
    body: Partial<Pick<UserGroup, 'name' | 'color' | 'icon' | 'position'>>
  ): Promise<UserGroup> => {
    const response = await apiClient.patch<UserGroup>(`/user-groups/${groupId}`, body);
    return response.data;
  },

  deleteGroup: async (groupId: string): Promise<void> => {
    await apiClient.delete(`/user-groups/${groupId}`);
  },

  // ── Subgroups ─────────────────────────────────────────────────────────────

  createSubgroup: async (
    groupId: string,
    body: { name: string; position?: number }
  ): Promise<Subgroup> => {
    const response = await apiClient.post<Subgroup>(`/user-groups/${groupId}/subgroups`, body);
    return response.data;
  },

  updateSubgroup: async (
    groupId: string,
    subgroupId: string,
    body: { name?: string; position?: number }
  ): Promise<Subgroup> => {
    const response = await apiClient.patch<Subgroup>(
      `/user-groups/${groupId}/subgroups/${subgroupId}`,
      body
    );
    return response.data;
  },

  deleteSubgroup: async (groupId: string, subgroupId: string): Promise<void> => {
    await apiClient.delete(`/user-groups/${groupId}/subgroups/${subgroupId}`);
  },

  // ── Agent assignment ─────────────────────────────────────────────────────

  /**
   * Set or clear the per-admin group+subgroup for an agent.
   * Empty body (or both fields null) clears the assignment → agent → "(Sin grupo)".
   * Cross-user attempts surface as 409.
   */
  updateAgentAssignment: async (
    agentId: string,
    body: UpdateAssignmentRequest
  ): Promise<AgentAssignment> => {
    const response = await apiClient.put<AgentAssignment>(`/agents/${agentId}/assignment`, body);
    return response.data;
  },
};
